import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

export function useCompanies({
  filters = {},
  page = 1,
  limit = 25,
  sort = "-createdAt",
} = {}) {
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchCompanies = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sort", sort);
      // SOUND-295: request the per-row summary embed (childCount, midCount,
      // completenessSummary) so the list renders in a single round-trip.
      params.set("include", "summary");

      if (filters.search) params.set("search", filters.search);
      if (filters.companyType) params.set("companyType", filters.companyType);
      if (filters.status) params.set("status", filters.status);
      if (filters.tier) params.set("tier", filters.tier);
      // SOUND-295: source / applicationStatus / partnerId are now applied
      // server-side (filter pushdown), so filtering is cross-page and
      // pagination.total is the post-filter count.
      if (filters.source) params.set("source", filters.source);
      if (filters.applicationStatus)
        params.set("applicationStatus", filters.applicationStatus);
      if (filters.partnerId) params.set("partnerId", filters.partnerId);

      const response = await makeApiRequest(
        "get",
        `${apiRoutes.getCompanies}?${params.toString()}`,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );

      const body = response?.data || {};
      setCompanies(Array.isArray(body.data) ? body.data : []);
      setPagination(body.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    page,
    limit,
    sort,
    filters.search,
    filters.companyType,
    filters.status,
    filters.tier,
    filters.source,
    filters.applicationStatus,
    filters.partnerId,
  ]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  return { companies, pagination, loading, error, refetch: fetchCompanies };
}
